import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Save, Copy, Download, Share2, Check, Loader2 } from "lucide-react";
import jsPDF from "jspdf";

/**
 * Actions: Save (history), Copy (clipboard), PDF (export), Share (URL).
 * Props:
 *   - type: 'orbital' | 'manual_tig'
 *   - inputs: { ... }
 *   - outputs: { ... }
 *   - shareParams: { key: value } -> appended to URL like ?m=...
 *   - title: human title for PDF
 */
export default function CalcActions({ type, inputs, outputs, shareParams, title }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  const save = async () => {
    if (!user) { alert("Pre uloženie sa najprv prihlás."); return; }
    try {
      await api.post("/calc/history", { type, inputs, outputs, label: title });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert("Uloženie zlyhalo: " + (e.response?.data?.detail || e.message));
    }
  };

  const copy = async () => {
    const lines = [`${title}`, "─".repeat(40), ...Object.entries(outputs).flatMap(([k, v]) => {
      if (Array.isArray(v)) return [`${k}: ${v.length} rows`];
      if (typeof v === "object" && v !== null) return [];
      return [`${k}: ${v}`];
    })];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      alert("Kopírovanie zlyhalo.");
    }
  };

  const exportPdf = () => {
    setSavingPdf(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const gold = [212, 175, 55];      // #D4AF37 SauberEko gold
      const ink  = [20, 20, 20];

      // Header bar (black) with gold accent
      doc.setFillColor(8, 8, 8);
      doc.rect(0, 0, 595, 56, "F");
      doc.setFillColor(...gold);
      doc.rect(0, 56, 595, 3, "F");

      doc.setTextColor(...gold);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.text("TIGIK", 40, 36);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(200, 200, 200);
      doc.text("A product under the SauberEko® brand", 40, 50);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(200, 200, 200);
      doc.text(new Date().toLocaleString(), 555, 36, { align: "right" });

      // Diagonal SauberEko watermark
      doc.setTextColor(245, 245, 245);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(72);
      // jsPDF angle: positive = ccw
      doc.text("SAUBEREKO", 90, 520, { angle: 28 });

      // Title
      doc.setTextColor(...ink);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text(title, 40, 96);

      // Gold underline
      doc.setDrawColor(...gold);
      doc.setLineWidth(1.5);
      doc.line(40, 104, 200, 104);

      // Inputs
      let y = 130;
      doc.setTextColor(...ink);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Inputs", 40, y); y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      Object.entries(inputs).forEach(([k, v]) => {
        doc.text(`${k}: ${v}`, 50, y); y += 16;
      });

      // Outputs
      y += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Results", 40, y); y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      Object.entries(outputs).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          doc.setFont("helvetica", "bold");
          doc.text(`${k}:`, 50, y); y += 14;
          doc.setFont("helvetica", "normal");
          v.forEach((row) => {
            const line = Object.entries(row).map(([rk, rv]) => `${rk}=${rv}`).join("  ");
            doc.text(line, 60, y); y += 13;
            if (y > 760) { doc.addPage(); y = 80; }
          });
        } else if (typeof v === "object" && v !== null) {
          // skip
        } else {
          doc.text(`${k}: ${v}`, 50, y); y += 14;
          if (y > 760) { doc.addPage(); y = 80; }
        }
      });

      // Footer band
      doc.setFillColor(8, 8, 8);
      doc.rect(0, 800, 595, 42, "F");
      doc.setFillColor(...gold);
      doc.rect(0, 800, 595, 2, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...gold);
      doc.text("Tigik · SauberEko®", 40, 818);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(200, 200, 200);
      doc.text("info@saubereko.eu  ·  tigik.saubereko.eu", 40, 832);
      doc.text("ORIENTAČNÉ HODNOTY · Nezaručujú 100% kvalitu zvaru. Vždy validuj na vzorovom kuse.", 555, 832, { align: "right" });

      const filename = `tigik-${type}-${Date.now()}.pdf`;
      doc.save(filename);
    } finally {
      setSavingPdf(false);
    }
  };

  const share = async () => {
    const params = new URLSearchParams(shareParams || {});
    const path = type === "orbital" ? "/calculators/orbital" : "/calculators/manual-tig";
    const url = `${window.location.origin}${path}?${params.toString()}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Tigik výpočet", text: title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (e) {
      // user cancelled or no permission
    }
  };

  return (
    <div className="flex flex-wrap gap-2 p-4 bg-[var(--wb-bg)] border-t border-zinc-800" data-testid="calc-actions">
      <button onClick={save} data-testid="calc-save-btn"
        className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5">
        {saved ? <><Check className="w-3.5 h-3.5 text-green-500" /> Uložené</> : <><Save className="w-3.5 h-3.5" /> Uložiť</>}
      </button>
      <button onClick={copy} data-testid="calc-copy-btn"
        className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5">
        {copied ? <><Check className="w-3.5 h-3.5 text-green-500" /> Kopírované</> : <><Copy className="w-3.5 h-3.5" /> Kopírovať</>}
      </button>
      <button onClick={exportPdf} disabled={savingPdf} data-testid="calc-pdf-btn"
        className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5">
        {savingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        PDF
      </button>
      <button onClick={share} data-testid="calc-share-btn"
        className="wb-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5">
        {shared ? <><Check className="w-3.5 h-3.5 text-green-500" /> Skopírované</> : <><Share2 className="w-3.5 h-3.5" /> Zdieľať</>}
      </button>
    </div>
  );
}
